import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { TFunction } from "i18next";
import Text from "@/components/ui/text";
import { isoToShortDate } from "@/utils/datetime";
import {
  REPORT_ICON_COLUMN_SIZE,
  ReportIcon,
  ScheduleSummaryCell
} from "@/components/global/Chat/components/dreamerReports/reportTableCells";
import {
  ReportActionsCell,
  ReportNameCell
} from "@/components/global/Chat/components/dreamerReports/ExpandedReportCells";
import { SavedReport } from "@/entities/savedReports/types";
import {
  EXPANDED_TRANSLATION_KEY,
  TRANSLATION_KEY
} from "@/components/global/Chat/components/dreamerReports/consts";

interface UseExpandedReportColumnsArgs {
  t: TFunction;
  renameTargetReportId: string | null;
  onOpen: (report: SavedReport) => void;
  onStartRename: (report: SavedReport) => void;
  onFinishRename: () => void;
  onSchedule: (report: SavedReport) => void;
  onRemoveSchedule: (report: SavedReport) => void;
  onDelete: (report: SavedReport) => void;
}

/**
 * Columns of the expanded reports table. Sorting is off on every column: the
 * list is paginated server-side and the filter endpoint takes no sort field, so
 * a client-side sort would only reorder the pages already fetched.
 */
export const useExpandedReportColumns = ({
  t,
  renameTargetReportId,
  onOpen,
  onStartRename,
  onFinishRename,
  onSchedule,
  onRemoveSchedule,
  onDelete
}: UseExpandedReportColumnsArgs): ColumnDef<SavedReport, unknown>[] =>
  useMemo(
    () => [
      {
        id: "icon",
        header: "",
        enableSorting: false,
        size: REPORT_ICON_COLUMN_SIZE,
        cell: ({ row }) => <ReportIcon isScheduled={row.original.isScheduled} />
      },
      {
        id: "name",
        accessorKey: "name",
        header: t(`${TRANSLATION_KEY}.nameHeader`),
        enableSorting: false,
        size: 320,
        cell: ({ row }) => (
          <ReportNameCell
            report={row.original}
            isRenaming={row.original.id === renameTargetReportId}
            onFinishRename={onFinishRename}
          />
        )
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: t(`${EXPANDED_TRANSLATION_KEY}.createdAt`),
        enableSorting: false,
        cell: ({ row }) => (
          <Text as="div">{isoToShortDate(row.original.createdAt)}</Text>
        )
      },
      {
        id: "schedule",
        header: t(`${EXPANDED_TRANSLATION_KEY}.schedule`),
        enableSorting: false,
        cell: ({ row }) => (
          <ScheduleSummaryCell
            t={t}
            variant="bodySecondary"
            scheduleConfig={
              row.original.isScheduled ? row.original.scheduleConfig : null
            }
            emptyText={t(`${EXPANDED_TRANSLATION_KEY}.notScheduled`)}
          />
        )
      },
      {
        id: "nextRunAt",
        accessorKey: "nextRunAt",
        header: t(`${EXPANDED_TRANSLATION_KEY}.nextDelivery`),
        enableSorting: false,
        // Empty for a report that is not scheduled — there is no next delivery
        // to name, and the Schedule column beside it already says so in words.
        cell: ({ row }) => (
          <Text as="div">
            {row.original.nextRunAt
              ? isoToShortDate(row.original.nextRunAt)
              : ""}
          </Text>
        )
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        size: 140,
        cell: ({ row }) => (
          <ReportActionsCell
            report={row.original}
            onOpen={onOpen}
            onStartRename={onStartRename}
            onSchedule={onSchedule}
            onRemoveSchedule={onRemoveSchedule}
            onDelete={onDelete}
          />
        )
      }
    ],
    [
      t,
      renameTargetReportId,
      onOpen,
      onStartRename,
      onFinishRename,
      onSchedule,
      onRemoveSchedule,
      onDelete
    ]
  );
